import React, { useState } from 'react';
import { FieldStructure } from '@mercell/form-react';
import { Input } from '@mercell/input-react';
import FieldError from '@mercell/field-error-react';
import cx from 'classnames';

const deduplicateMessages = (messages: string[]): string | null => {
    const uniqueMessages = messages.filter(
        (msg, index, self) => index === self.indexOf(msg)
    );
    return uniqueMessages.join('\n') || null;
};

const removeEmptyLines = (lines: string[]): string[] =>
    lines.map((line) => line.trim()).filter((line) => line !== '');

/**
 * Normalize a field error value to a string suitable for rendering.
 * Handles strings, string arrays, and corrupted error objects (e.g. {0:'R', 1:'e', ...}).
 * Deduplicates repeated messages like "Required\nRequired" → "Required".
 */
export const normalizeFieldError = (
    error: unknown
): string | null => {
    if (!error) return null;

    if (typeof error === 'string') {
        const errorLines = removeEmptyLines(error.split('\n'));
        return deduplicateMessages(errorLines);
    }

    if (
        Array.isArray(error) &&
        error.every((v) => typeof v === 'string')
    ) {
        const nonEmptyErrors = removeEmptyLines(error);
        return deduplicateMessages(nonEmptyErrors);
    }

    // Handle corrupted error objects (string spread into object with numeric keys)
    if (typeof error === 'object' && !Array.isArray(error)) {
        const keys = Object.keys(error);
        if (keys.length > 0 && keys.every((k) => /^\d+$/.test(k))) {
            const reconstructed = keys
                .sort((a, b) => Number(a) - Number(b))
                .map((k) => (error as Record<string, string>)[k])
                .join('');
            return reconstructed.trim() || null;
        }
    }

    return null;
};

/**
 * Normalize a numeric string to use '.' as decimal separator and no thousand separators
 * Handles various locale formats:
 * - Swedish: 1 234,56 → 1234.56
 * - German: 1.234,56 → 1234.56
 * - English: 1,234.56 → 1234.56
 */
export const normalizeNumberString = (value: string): string => {
    if (!value || value === '') {
        return '';
    }

    // Remove all spaces (thousand separators in many locales)
    let normalized = value.replace(/\s/g, '');

    // Count occurrences of comma and dot
    const commaCount = (normalized.match(/,/g) || []).length;
    const dotCount = (normalized.match(/\./g) || []).length;

    // Multiple commas = English thousand separators (e.g., 1,234,567)
    if (commaCount > 1) {
        normalized = normalized.replace(/,/g, '');
    }
    // Multiple dots = German thousand separators (e.g., 1.234.567 or 1.234.567,89)
    else if (dotCount > 1) {
        // Remove dots (thousand separators) and convert comma to dot (decimal separator)
        normalized = normalized.replace(/\./g, '').replace(',', '.');
    }
    // Both comma and dot exist (one each): the last one is the decimal separator
    else if (commaCount === 1 && dotCount === 1) {
        const lastCommaIndex = normalized.lastIndexOf(',');
        const lastDotIndex = normalized.lastIndexOf('.');

        if (lastCommaIndex > lastDotIndex) {
            // Comma is decimal separator (e.g., German: 1.234,56)
            normalized = normalized.replace(/\./g, '').replace(',', '.');
        } else {
            // Dot is decimal separator (e.g., English: 1,234.56)
            normalized = normalized.replace(/,/g, '');
        }
    }
    // Single comma, no dot: check if comma is thousand separator or decimal separator
    else if (commaCount === 1 && dotCount === 0) {
        // Check digits after comma - if exactly 3 digits and the part before is NOT just "0",
        // it's likely a thousand separator (e.g., "100,000" = 100000)
        // If part before is "0" or "-0", it's a decimal (e.g., "0,123" = 0.123)
        // Otherwise, it's a decimal separator (e.g., "100,5" = 100.5)
        const parts = normalized.split(',');
        const beforeComma = parts[0].replace(/^-/, ''); // Remove leading minus for comparison
        if (
            parts.length === 2 &&
            parts[1].length === 3 &&
            beforeComma.length >= 1 &&
            beforeComma !== '0'
        ) {
            // Thousand separator - remove it
            normalized = normalized.replace(',', '');
        } else {
            // Decimal separator - convert to dot
            normalized = normalized.replace(',', '.');
        }
    }
    // Single dot, no comma: check if dot is thousand separator or decimal separator
    else if (dotCount === 1 && commaCount === 0) {
        // Check digits after dot - if exactly 3 digits and the part before is NOT just "0",
        // it's likely a thousand separator (e.g., "100.000" in German = 100000)
        // If part before is "0" or "-0", it's a decimal (e.g., "0.123" = 0.123)
        // Otherwise, it's a decimal separator (e.g., "100.5" = 100.5)
        const parts = normalized.split('.');
        const beforeDot = parts[0].replace(/^-/, ''); // Remove leading minus for comparison
        if (
            parts.length === 2 &&
            parts[1].length === 3 &&
            beforeDot.length >= 1 &&
            beforeDot !== '0'
        ) {
            // Thousand separator - remove it
            normalized = normalized.replace('.', '');
        }
        // else: Decimal separator - already in correct format
    }
    // No separators: format is already correct

    return normalized;
};

/**
 * Check if a string represents a valid numeric value
 * Valid: "123", "123.45", "-123", "-123.45", "0.123", ".5"
 * Invalid: "1-2", "1.2.3", "abc", "1a2"
 */
export const isValidNumericString = (value: string): boolean => {
    if (!value || value === '') return true;
    return /^-?(?:\d+\.?\d*|\.\d+)$/.test(value);
};

export const formatNumberForLocale = (
    value: number | string | null | undefined,
    locale: string
): string => {
    if (value === null || value === undefined || value === '') {
        return '';
    }

    if (typeof value === 'string' && !isValidNumericString(value)) {
        return value;
    }

    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (Number.isNaN(numValue)) {
        return typeof value === 'string' ? value : '';
    }

    return new Intl.NumberFormat(locale, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 10,
        useGrouping: true,
    }).format(numValue);
};

interface NumericFieldRenderOptions<T> {
    locale: string;
    genericFieldStructure: FieldStructure;
    parseValue: (value: string) => T;
    formatForEditing: (value: T) => string;
}

/**
 * Creates a custom render function for numeric fields with focus/blur formatting
 * - When focused: shows raw value with '.' as decimal, no thousand separators
 * - When blurred: shows locale-formatted value with proper separators
 */
export const createNumericFieldRender = <T,>({
    locale,
    genericFieldStructure,
    parseValue,
    formatForEditing,
}: NumericFieldRenderOptions<T>) =>
    // eslint-disable-next-line react/display-name
    (props: any) => {
        const { input, meta } = props;
        const [isFocused, setIsFocused] = useState(false);

        const rawValue = input.value;
        const displayValue = isFocused
            ? formatForEditing(rawValue)
            : formatNumberForLocale(rawValue, locale);

        const fieldError = normalizeFieldError(meta.error) || normalizeFieldError(meta.submitError);
        const showErrors =
            ((meta.touched || meta.submitFailed) && fieldError && !meta.active) ||
            (meta.submitError && meta.submitFailed);

        const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
            setIsFocused(true);
            input.onFocus(e);
        };

        const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
            setIsFocused(false);
            const parsed = parseValue(e.target.value);
            input.onChange(parsed);
            input.onBlur(e);
        };

        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            input.onChange(e.target.value);
        };

        const elementProperties = genericFieldStructure.elementProperties || {};
        const fieldWrapperProperties =
            genericFieldStructure.fieldWrapperProperties || {};

        return (
            <div {...fieldWrapperProperties} data-field={input.name}>
                {showErrors && <FieldError>{fieldError}</FieldError>}
                {genericFieldStructure.fieldLabel &&
                    genericFieldStructure.fieldLabel(
                        (elementProperties as any)?.id || input.name,
                        false
                    )}
                <Input
                    {...elementProperties}
                    id={(elementProperties as any)?.id || input.name}
                    name={input.name}
                    type="text"
                    value={displayValue}
                    onChange={handleChange}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    disabled={
                        (elementProperties as any)?.disabled ||
                        genericFieldStructure.disabled
                    }
                    aria-required={genericFieldStructure.ariaRequired}
                    aria-invalid={showErrors}
                    invalid={showErrors}
                    placeholder={genericFieldStructure.placeholder}
                    className={cx((elementProperties as any)?.className)}
                />
            </div>
        );
    };
